import { LessonDefinition } from "../../types";
import { SAMPLES } from "../../samples";

const lesson: LessonDefinition = {
  id: "first-narrative-report",
  courseId: "getting-started",
  order: 3,
  title: "Your first report",
  subtitle: "From a narrative recording",
  summary:
    "Upload a recording of yourself talking through a call and get back a scored draft with suggestions. The sample file on this page works if you do not have a recording handy.",
  learn: [
    "Upload a narrative recording through New Case",
    "Confirm the file type and the report settings before you submit",
    "Recognize the processing screen and the finished draft",
  ],
  minutes: 5,
  releasedAt: "2026-10-28T00:00:00.000Z",
  sceneId: "first-narrative-report",
  tourId: "first-narrative-report",
  sample: SAMPLES.narrativeRetailTheft,
  body: [
    {
      type: "paragraph",
      text: "A narrative recording is you talking through the call in your own words, the way you would dictate it after clearing. The AI transcribes it and writes the report in your preferred perspective and format. This is the flow you will use most.",
    },
    {
      type: "callout",
      tone: "tip",
      title: "Use the sample",
      text: "Download the sample dictation from the card on this page. It is a fictional retail theft at Valley Mart, with a loss-prevention witness named Daniel Reyes and a subject named Marcus Pierce. Your draft should name all three. The dictation never states your arrival time, so expect a placeholder or a suggestion asking for it.",
    },
    {
      type: "steps",
      items: [
        "Click **New Case** in the sidebar and choose **Upload Files**.",
        "Drop the file onto \"Choose files or drag and drop\", or click to pick it. MP3, MP4, M4A, and WAV files under 4 hours are accepted.",
        "Check the file row and make sure **Narrative** is selected.",
        "Optional: open **Report Settings** and look at **Report Template**, **Report Detail Level** (Concise, Standard, Detailed), and **Include timestamps in narrative**. The defaults are fine for a first run.",
        "Click **Upload (1 file)**.",
        "Wait on the processing screen. The note \"You can safely leave this page. Processing will continue in the background.\" means what it says.",
        "When the draft appears, read it, then look at the score and the **Suggestions** tab on the right.",
      ],
    },
    {
      type: "paragraph",
      text: "Files over 75 MB, and any video, default to **Interaction** instead of **Narrative**. A short MP3 like the sample stays on Narrative, but check the row whenever you upload something larger.",
    },
    { type: "heading", text: "What you should see" },
    {
      type: "bullets",
      items: [
        "A narrative written in your perspective, date format, and time format.",
        "A report score with a label such as Satisfactory or Needs More Detail, plus a short list of suggestions.",
        "A **Transcription** tab beside **Narrative**, with the transcript synced to the audio.",
      ],
    },
    {
      type: "callout",
      tone: "note",
      text: "Treat the draft as a starting point, not a final report. The next lesson, Review the draft, covers the suggestions, the score, and the bracketed placeholders the AI leaves where it did not have a fact.",
    },
  ],
  related: ["review-the-draft", "narrative-vs-interaction-audio", "set-up-your-report-preferences"],
};

export default lesson;
